/**
 * @fileoverview Performance Utilities
 * @description Client-side performance monitoring and optimization utilities
 */

/**
 * Performance monitoring class
 */
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.observers = new Map();
    this.isSupported = 'performance' in window;
    this.initialized = false;
    this.config = {};
  }

  /**
   * Initialize performance monitoring
   * @param {Object} config - Configuration options
   */
  init(config = {}) {
    this.config = {
      enableMetrics: true,
      enableUserTiming: true,
      enableResourceTiming: true,
      ...config
    };
    this.initialized = true;
    
    if (this.config.enableUserTiming && this.isSupported) {
      this.mark('performance-monitor-initialized');
    }
  }

  /**
   * Create a performance mark
   * @param {string} name - Mark name
   */
  mark(name) {
    if (!this.isSupported || !this.initialized) return;
    
    try {
      performance.mark(name);
    } catch (error) {
      console.warn('Failed to create performance mark:', error);
    }
  }

  /**
   * Cleanup performance monitoring
   */
  cleanup() {
    this.clear();
    this.initialized = false;
  }

  /**
   * Start timing a performance metric
   * @param {string} name - Metric name
   */
  startTiming(name) {
    if (!this.isSupported) return;
    
    const startTime = performance.now();
    this.metrics.set(name, { startTime, endTime: null, duration: null });
  }

  /**
   * End timing a performance metric
   * @param {string} name - Metric name
   * @returns {number} Duration in milliseconds
   */
  endTiming(name) {
    if (!this.isSupported) return 0;
    
    const metric = this.metrics.get(name);
    if (!metric) return 0;
    
    const endTime = performance.now();
    const duration = endTime - metric.startTime;
    
    this.metrics.set(name, {
      ...metric,
      endTime,
      duration
    });
    
    return duration;
  }

  /**
   * Get metric duration
   * @param {string} name - Metric name
   * @returns {number} Duration in milliseconds
   */
  getMetric(name) {
    const metric = this.metrics.get(name);
    return metric ? metric.duration : 0;
  }

  /**
   * Get all metrics
   * @returns {Object} All metrics
   */
  getAllMetrics() {
    const result = {};
    this.metrics.forEach((value, key) => {
      result[key] = value.duration;
    });
    return result;
  }

  /**
   * Observe Largest Contentful Paint (LCP)
   * @param {Function} callback - Callback function
   */
  observeLCP(callback) {
    if (!this.isSupported || !('PerformanceObserver' in window)) return;
    
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      callback(lastEntry.startTime);
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
    this.observers.set('lcp', observer);
  }

  /**
   * Observe First Input Delay (FID)
   * @param {Function} callback - Callback function
   */
  observeFID(callback) {
    if (!this.isSupported || !('PerformanceObserver' in window)) return;
    
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        callback(entry.processingStart - entry.startTime);
      });
    });
    
    observer.observe({ entryTypes: ['first-input'] });
    this.observers.set('fid', observer);
  }

  /**
   * Observe Cumulative Layout Shift (CLS)
   * @param {Function} callback - Callback function
   */
  observeCLS(callback) {
    if (!this.isSupported || !('PerformanceObserver' in window)) return;
    
    let clsValue = 0;
    let clsEntries = [];
    
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            clsEntries.push(entry);
            callback(clsValue);
          }
        });
      });
      
      observer.observe({ entryTypes: ['layout-shift'] });
      this.observers.set('cls', observer);
    } catch (error) {
      console.warn('Ignoring unsupported entryTypes: layout-shift.');
      console.warn('No valid entryTypes; aborting registration.');
    }
  }

  /**
   * Get navigation timing metrics
   * @returns {Object} Navigation timing metrics
   */
  getNavigationMetrics() {
    if (!this.isSupported) return {};
    
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!navigation) return {};
    
    return {
      dns: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcp: navigation.connectEnd - navigation.connectStart,
      ssl: navigation.secureConnectionStart > 0 
        ? navigation.connectEnd - navigation.secureConnectionStart 
        : 0,
      ttfb: navigation.responseStart - navigation.requestStart,
      download: navigation.responseEnd - navigation.responseStart,
      domParse: navigation.domContentLoadedEventStart - navigation.responseEnd,
      domReady: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      total: navigation.loadEventEnd - navigation.navigationStart
    };
  }

  /**
   * Get resource timing metrics
   * @returns {Array} Resource timing metrics
   */
  getResourceMetrics() {
    if (!this.isSupported) return [];
    
    const resources = performance.getEntriesByType('resource');
    return resources.map(resource => ({
      name: resource.name,
      type: resource.initiatorType,
      size: resource.transferSize,
      duration: resource.duration,
      startTime: resource.startTime
    }));
  }

  /**
   * Clear all metrics and observers
   */
  clear() {
    this.metrics.clear();
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }

  /**
   * Track error for monitoring
   * @param {Error} error - The error object
   * @param {Object} context - Additional context information
   */
  trackError(error, context = {}) {
    if (!this.initialized) return;
    
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      ...context
    };
    
    // Store error in metrics
    const errorKey = `error-${Date.now()}`;
    this.metrics.set(errorKey, errorData);
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Performance Monitor - Error tracked:', errorData);
    }
    
    // In production, you might want to send this to an error tracking service
    // Example: sendToErrorService(errorData);
  }
}

/**
 * Memory usage monitoring
 */
export class MemoryMonitor {
  constructor() {
    this.isSupported = 'memory' in performance;
  }

  /**
   * Get current memory usage
   * @returns {Object} Memory usage metrics
   */
  getMemoryUsage() {
    if (!this.isSupported) return null;
    
    const memory = performance.memory;
    return {
      used: Math.round(memory.usedJSHeapSize / 1048576), // MB
      total: Math.round(memory.totalJSHeapSize / 1048576), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1048576), // MB
      percentage: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)
    };
  }

  /**
   * Monitor memory usage over time
   * @param {Function} callback - Callback function
   * @param {number} interval - Monitoring interval in ms
   * @returns {number} Interval ID
   */
  startMonitoring(callback, interval = 5000) {
    return setInterval(() => {
      const usage = this.getMemoryUsage();
      if (usage) callback(usage);
    }, interval);
  }

  /**
   * Stop memory monitoring
   * @param {number} intervalId - Interval ID to clear
   */
  stopMonitoring(intervalId) {
    clearInterval(intervalId);
  }
}

/**
 * Image lazy loading utility
 */
export class LazyImageLoader {
  constructor() {
    this.observer = null;
    this.images = new Set();
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        {
          rootMargin: '50px 0px',
          threshold: 0.01
        }
      );
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        this.loadImage(img);
        this.observer.unobserve(img);
        this.images.delete(img);
      }
    });
  }

  loadImage(img) {
    const src = img.dataset.src;
    if (src) {
      img.src = src;
      img.removeAttribute('data-src');
      img.classList.add('loaded');
    }
  }

  observe(img) {
    if (this.observer) {
      this.observer.observe(img);
      this.images.add(img);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.loadImage(img);
    }
  }

  unobserve(img) {
    if (this.observer) {
      this.observer.unobserve(img);
      this.images.delete(img);
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.images.clear();
    }
  }
}

/**
 * Component performance wrapper
 */
export function withPerformanceTracking(WrappedComponent, componentName) {
  return function PerformanceTrackedComponent(props) {
    const monitor = new PerformanceMonitor();
    
    React.useEffect(() => {
      monitor.startTiming(`${componentName}-mount`);
      
      return () => {
        const mountTime = monitor.endTiming(`${componentName}-mount`);
        console.log(`${componentName} mount time: ${mountTime}ms`);
      };
    }, []);
    
    return React.createElement(WrappedComponent, props);
  };
}

/**
 * Debounce utility for performance optimization
 */
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle utility for performance optimization
 */
export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Bundle analyzer utility
 */
export function analyzeBundleSize() {
  if (!('performance' in window)) return;
  
  const resources = performance.getEntriesByType('resource');
  const jsResources = resources.filter(r => r.name.endsWith('.js'));
  const cssResources = resources.filter(r => r.name.endsWith('.css'));
  
  const totalJSSize = jsResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
  const totalCSSSize = cssResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
  
  console.group('Bundle Analysis');
  console.log(`Total JS Size: ${(totalJSSize / 1024).toFixed(2)} KB`);
  console.log(`Total CSS Size: ${(totalCSSSize / 1024).toFixed(2)} KB`);
  console.log(`Total Bundle Size: ${((totalJSSize + totalCSSSize) / 1024).toFixed(2)} KB`);
  console.log('JS Resources:', jsResources.map(r => ({
    name: r.name.split('/').pop(),
    size: `${(r.transferSize / 1024).toFixed(2)} KB`
  })));
  console.groupEnd();
}

// Create global instances
export const performanceMonitor = new PerformanceMonitor();
export const memoryMonitor = new MemoryMonitor();
export const lazyImageLoader = new LazyImageLoader();

// Auto-start core web vitals monitoring in development
if (process.env.NODE_ENV === 'development') {
  performanceMonitor.observeLCP((lcp) => {
    console.log(`LCP: ${lcp}ms`);
  });
  
  performanceMonitor.observeFID((fid) => {
    console.log(`FID: ${fid}ms`);
  });
  
  performanceMonitor.observeCLS((cls) => {
    console.log(`CLS: ${cls}`);
  });
}